/**
 * SS58 Address Utilities
 * Shared utilities for validating and working with Substrate SS58 addresses
 */
import { decodeAddress, encodeAddress } from '@polkadot/util-crypto';

/** Regex pattern to detect potential SS58 addresses (46-48 chars, base58 alphabet) */
const SS58_ADDRESS_REGEX = /^[1-9A-HJ-NP-Za-km-z]{46,48}$/;

/**
 * Check if a string is a valid SS58 address
 * Uses both regex pattern matching and actual decoding validation
 */
export function isValidSS58Address(address: string): boolean {
	if (!SS58_ADDRESS_REGEX.test(address)) {
		return false;
	}
	try {
		decodeAddress(address);
		return true;
	} catch {
		return false;
	}
}

/**
 * Convert any SS58 address to AccountId32 (raw 32-byte public key)
 * This is chain-agnostic - same address on all chains has same AccountId32
 *
 * @param address - Any valid SS58 address (Polkadot, Kusama, generic, etc.)
 * @returns Uint8Array of 32 bytes (AccountId32)
 */
export function getAccountId32(address: string): Uint8Array {
	return decodeAddress(address);
}

/**
 * Re-encode an address with a specific SS58 prefix
 *
 * @param address - Any valid SS58 address
 * @param prefix - SS58 prefix (0 = Polkadot, 2 = Kusama, 42 = generic)
 * @returns Re-encoded address string
 */
export function reencodeAddress(address: string, prefix: number): string {
	const accountId = decodeAddress(address);
	return encodeAddress(accountId, prefix);
}
